import axios from 'axios';
import httpClient from "../../clients/httpClient";
import { IOrganization } from "../../interfaces/organization.interface";

// Raw-axios fetch of the tenant project's attributes, bypassing httpClient.
// httpClient dedupes concurrent requests to the same URL by aborting the
// earlier one (see clients/httpClient AbortController map), and an aborted
// request resolves to a never-settling promise. Layout-level consumers
// (platform lock, paused banner) share the /organizations/project endpoint with
// the dashboard page, so they must fetch outside httpClient or the dashboard's
// own project fetch on /overview would abort theirs and hang forever.
const fetchProjectAttributesRaw = async (): Promise<Record<string, any> | null> => {
    try {
        const org = localStorage.getItem('org');
        const protocol = import.meta.env.VITE_BACKEND_PROTOCOL ?? 'https';
        const baseURL = org
            ? `${protocol}://${org}.${import.meta.env.VITE_ORG_API_ENDPOINT}`
            : '';
        const token = JSON.parse(localStorage.getItem('token') || '{}')?.access_token;
        const response = await axios.get(
            `${baseURL}/api/v1/organizations/project`,
            {
                headers: {
                    Authorization: token ? `Bearer ${token}` : '',
                    Accept: 'application/json',
                },
            }
        );
        return response.data?.data?.attributes ?? null;
    } catch {
        return null;
    }
};

export const getPlatformLockStatus = async (): Promise<boolean> => {
    const attributes = await fetchProjectAttributesRaw();
    return attributes?.platform_locked ?? false;
};

// Used by PausedBanner (rendered in SidebarLayout). Must stay a raw-axios call
// for the reason documented on fetchProjectAttributesRaw above — routing it
// through httpClient reintroduces the /overview abort-and-hang bug where the
// banner never appears until another page (e.g. /stores) is loaded first.
export const getProjectStatus = async (): Promise<string | null> => {
    const attributes = await fetchProjectAttributesRaw();
    return attributes?.project_status ?? null;
};

export const getOrganizationData = () => {
    return httpClient.get(`/api/v1/organizations`);
};

export const editOrganization = (payload: FormData) => {
    return httpClient.post<IOrganization>(`/api/v1/organizations`, payload, {
        params: {
            _method: "PATCH",
            include: 'tenantSettings'
        }
    });
};

export const getTenantProject = () =>
    httpClient.get("/api/v1/organizations/project");

// No body when it comes from Request a Revision — the round's Messages are the content
export const submitDesignFeedback = (feedback?: string) =>
    httpClient.post('/api/v1/organizations/project/design-feedback', { feedback: feedback ?? null });

export const approveDesign = (comment?: string) =>
    httpClient.post('/api/v1/organizations/project/design-approve', { comment: comment ?? null });

export const approveApp = (comment?: string) =>
    httpClient.post('/api/v1/organizations/project/app-approve', { comment: comment ?? null });

export const getTenantProjectHistory = (milestone?: string) =>
    httpClient.get(
        `/api/v1/organizations/project/history${milestone ? `?milestone=${milestone}` : ''}`
    );

export const getMilestoneSchedule = () =>
    httpClient.get('/api/v1/organizations/project/milestone-schedule');

export const getMilestoneScheduleHistory = () =>
    httpClient.get('/api/v1/organizations/project/milestone-schedule/history');

export const setMilestoneStartDate = (startDate: string, note: string, reported: boolean) =>
    httpClient.post('/api/v1/organizations/project/milestone-schedule/set-start-date', {
        start_date: startDate,
        note,
        reported,
    });

export const updateMilestoneCheckpoint = (
    checkpointId: number,
    date: string,
    markComplete: boolean,
    note: string,
    reported: boolean
) =>
    httpClient.patch(
        `/api/v1/organizations/project/milestone-schedule/checkpoints/${checkpointId}`,
        { date, mark_complete: markComplete, note, reported }
    );

// Checklist
export const getTenantChecklist = () =>
    httpClient.get('/api/v1/organizations/checklist');

export const completeTenantChecklistItem = (itemId: number) =>
    httpClient.post(`/api/v1/organizations/checklist/items/${itemId}/complete`);

export const reopenTenantChecklistItem = (itemId: number) =>
    httpClient.post(`/api/v1/organizations/checklist/items/${itemId}/reopen`);

export const updateTenantDeveloperAccount = (
    itemId: number,
    payload: { org_name?: string | null; invite_sent?: boolean }
) =>
    httpClient.patch(
        `/api/v1/organizations/checklist/items/${itemId}/developer-account`,
        payload
    );

// Integrations onboarding (tenant — read + update credentials only).
// Served by Tenant\IntegrationOnboardingController; the update body carries the
// credential map under `data` (was `credentials`).
export const getTenantIntegrations = () =>
    httpClient.get('/api/v1/organizations/integrations');

export const updateTenantIntegrationCredentials = (
    integrationId: number,
    data: Record<string, string | number | null>
) =>
    httpClient.patch(`/api/v1/organizations/integrations/${integrationId}`, {
        data,
    });

// App Store Listing (tenant)
export const getTenantAppStoreListing = () =>
    httpClient.get('/api/v1/organizations/app-store-listing');

export const updateTenantAppStoreListing = (payload: FormData) =>
    httpClient.post('/api/v1/organizations/app-store-listing', payload);

export const completeTenantAppStoreListing = () =>
    httpClient.post('/api/v1/organizations/app-store-listing/complete');

export const reopenTenantAppStoreListing = () =>
    httpClient.post('/api/v1/organizations/app-store-listing/reopen');

// Feedback (tenant)
export const getTenantFeedback = () =>
    httpClient.get('/api/v1/organizations/feedback');

export const submitTenantFeedback = (payload: FormData) =>
    httpClient.post('/api/v1/organizations/feedback', payload);

export const addTenantFeedbackReply = (feedbackId: number, payload: FormData) =>
    httpClient.post(`/api/v1/organizations/feedback/${feedbackId}/replies`, payload);

export const markTenantFeedbackRead = (feedbackId: number) =>
    httpClient.post(`/api/v1/organizations/feedback/${feedbackId}/read`);

export const closeTenantFeedback = (feedbackId: number) =>
    httpClient.post(`/api/v1/organizations/feedback/${feedbackId}/close`);

export const reopenTenantFeedback = (feedbackId: number) =>
    httpClient.post(`/api/v1/organizations/feedback/${feedbackId}/reopen`);

export const getTenantFeedbackItem = (feedbackId: number) =>
    httpClient.get(`/api/v1/organizations/feedback/${feedbackId}`);

// App Testing (tenant)
export const getAppTestingInfo = () =>
    httpClient.get('/api/v1/organizations/app-testing');

export const addAndroidTester = (email: string) =>
    httpClient.post('/api/v1/organizations/app-testing/android-testers', { email });
